import { formatMoney } from "./money.js";
import { canSettle } from "./receivable.js";
import { foldForSearch } from "./search.js";
import { addDays, addMonths, dayOf, daysBetween } from "./period.js";
import { MAX_PAYMENT_TERM_DAYS } from "./limits.js";
import { offersQuotes } from "./faces.js";
import { hasModule } from "./modules.js";
/**
 * WHICH DOCUMENT THIS IS (0036). A quote is a piece of paper that was sent to
 * a customer on a date; a ticket is the work. They share one table and one
 * `order_line` -- see 0036's own comment for why a `quote_line` would have
 * been the third copy of «lines off a catalogue» and the first to drift.
 *
 * NOT THE SAME QUESTION AS `service_type`. That is the BUSINESS's face -- the
 * bakery's encargo, the workshop's servicio -- and this is the DOCUMENT: a
 * workshop's quotes and its tickets are both `'SERVICIO'`, and differ here.
 */
export const DOCUMENT_KINDS = ["QUOTE", "ORDER"];
/** Borrador → enviada → aceptada | rechazada | vencida. `EXPIRED` is READ, never written -- see `isExpired`. */
export const QUOTE_STATUSES = ["DRAFT", "SENT", "ACCEPTED", "REJECTED", "EXPIRED"];
/**
 * Abierto → cerrado | cancelado. Y ahí se acaba.
 *
 * THERE IS NO «FACTURADO»: «ya le pasé el papel y esto está cobrado» is
 * already said by the two things the app really tracks -- the ticket is
 * CLOSED, and its money is in the Libro. An electronic invoice is the
 * Facturación add-on's, not a status.
 *
 * WHAT ENDS A TICKET IS A MOVEMENT, NOT A STATUS. `deliverDocument` writes
 * nothing to the book; `Cobrar` does. So a finished ticket is DELIVERED with
 * `paidCents === priceCents`, which is a fact about money rather than a box
 * somebody has to remember to tick.
 */
export const WORK_STATUSES = ["PENDING", "DELIVERED", "CANCELLED"];
export const MAX_RECIPIENT_LENGTH = 120;
export const MAX_DESCRIPTION_LENGTH = 500;
/**
 * A line's or an article's long description, AS TYPED:
 * the boxes stop there, and the paper prints it whole. The server keeps no
 * cap -- a line copied from an imported article carries a longer text back on
 * every save (`checkLongDescription`).
 */
export const MAX_LONG_DESCRIPTION_LENGTH = 500;
export const MAX_ORDER_LINE_NAME_LENGTH = 160;
export const MAX_WORK_DONE_LENGTH = 2000;
/**
 * Whoever was chosen last (each client keeps the raw id in its own storage),
 * but only while they are still somebody the form may offer -- on the list of
 * who may be assigned, which holds only the active.
 *
 * A STORED ID IS A GUESS, NOT A FACT. The person may have been given de baja
 * or deleted since, and the business may not even be the one this device had
 * open last. Preselecting any of those puts a value in the box that has no
 * option to show it: the control draws blank while the form holds an id the
 * server will refuse. Forgetting is the honest answer; picking is one tap.
 */
export function lastWorkerAmong(raw, people) {
    if (raw === null || raw.trim() === "")
        return null;
    return people.some((person) => person.id === raw) ? raw : null;
}
/**
 * One line's total, discount applied -- ROUNDED TO THE NEAREST CENT, decided
 * ONCE here so the server and a client's own arithmetic can never land a
 * cent apart. The case that does not divide evenly: 33.33 × 3 al 15% is
 * 9999 × 0.85 = 8499.15, which rounds to 8499 -- see orders.test.ts.
 */
/**
 * A LINE'S LARGEST DISCOUNT, in whole percent. 100 gives the line away: it stays
 * on the paper at its price, discounted whole, instead of hiding at a zero
 * price. The DGI takes such a line inside an invoice that still charges (HKA
 * DEMO authorised it, `fiscal-hka-live.test.ts`); an invoice that totals zero
 * is still refused (`draftProblem`, NON_POSITIVE_TOTAL).
 */
const MAX_DISCOUNT_PCT = 100;
/**
 * What a discount box keeps of what was typed: digits only, at most three, and
 * capped at `MAX_DISCOUNT_PCT` as the person types -- a number that cannot be
 * written needs no rejecting later. Blank stays blank: a box cleared on the way
 * to «15» is not a request for zero.
 */
export function discountDigits(typed) {
    const digits = typed.replace(/[^0-9]/g, "").slice(0, 3);
    return digits === "" ? "" : String(Math.min(MAX_DISCOUNT_PCT, Number(digits)));
}
export function lineTotalCents(line) {
    return Math.round((line.priceCents * line.quantity * (100 - line.discountPct)) / 100);
}
/**
 * The list narrowed to one person («¿Quién?»): what was assigned to them, LIVE
 * ONLY. Its total (`/orders/tally`) never counts the cancelled, so the rows
 * beside it must not show them either, or «N ventas» and the rows would
 * disagree. Nobody chosen: no narrowing at all.
 */
export function personFilter(employeeId) {
    return employeeId === null ? {} : { employeeId, live: true };
}
/** «12 ventas · B/. 120.00»: what the list on screen adds up to. */
export function tallyLabel({ count, totalCents }) {
    return `${count} ${count === 1 ? "venta" : "ventas"} · ${formatMoney(totalCents)}`;
}
/**
 * Where each status may go next -- A TABLE, not a chain of `if`s, and THE
 * SAME table the server consults before it refuses. If the two ever diverged,
 * a screen would offer a button that answers 409.
 *
 * Keyed by status alone, because the two machines share no status: a quote is
 * never PENDING and a ticket is never SENT, and the database's own check
 * (0036) is what keeps that true. `kind` therefore adds nothing here.
 *
 * `EXPIRED` IS NEVER OFFERED, to anybody. Expiry is READ (`isExpired`), so
 * nothing ever writes that status -- and a quote that has expired is still
 * `SENT` underneath, which is exactly why it can still be accepted.
 *
 * `DELIVERED` IS A DEAD END, so the ticket's screen draws no «siguiente paso»
 * card: what is left for a ticket is PENDING's two, and BOTH of those have
 * their own door on the screen.
 */
const NEXT_STATUSES = {
    DRAFT: ["SENT"],
    SENT: ["ACCEPTED", "REJECTED"],
    // UNDOING THE CUSTOMER'S ANSWER. The customer answers and the business
    // writes it down, so a mistake is the writer's: going back to «esperando
    // respuesta» is a one-click undo, not a new state. `issued_on` is NOT
    // stamped again -- the paper went out on one day, see `setDocumentStatus`.
    //
    // AND THE OTHER ANSWER, IN ONE STEP: the state is changed in a selector,
    // and going from «Aceptada» to «Rechazada» through «Enviada» would be two
    // choices to correct one mistake.
    ACCEPTED: ["SENT", "REJECTED"],
    REJECTED: ["SENT", "ACCEPTED"],
    EXPIRED: [],
    PENDING: ["DELIVERED", "CANCELLED"],
    DELIVERED: [],
    CANCELLED: [],
};
/** The steps this document may take from where it is -- «el siguiente paso es uno, no cinco». */
export function nextStatuses(order) {
    return [...NEXT_STATUSES[order.status]];
}
/**
 * Folded, uppercased, and joined by dashes -- what `documentFileName` shouts
 * each part in.
 *
 * EXPORTED for the income receipt (`receipt.ts`), which builds its name from
 * other pieces but must fold and shout the same way: a file that reaches the
 * customer spelled two ways is the kind of detail nobody reports and
 * everybody notices.
 */
export function shout(value) {
    return foldForSearch(value).toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
/**
 * A NAME A PERSON RECOGNIZES, for whatever a cotización's or ticket's PDF is
 * saved or shared as -- «COT-A3F9C2-MARIA-RODRIGUEZ.pdf», not the id it uses
 * inside the app. `nameBase` is the word the document already carries
 * (`documentWordsFor(...).one`); only its first three letters make the
 * prefix, which is enough to tell «cotización» from «ticket» without
 * repeating the whole word.
 *
 * FOLDED BY `shout` WITH `foldForSearch`, NOT `foldAccents`: the two differ on
 * letters outside the search table, so switching would rename files people
 * already have.
 */
export function documentFileName(nameBase, code, recipient) {
    const parts = [shout(nameBase).slice(0, 3), shout(code), shout(recipient)].filter(Boolean);
    return `${parts.join("-")}.pdf`;
}
/**
 * WHAT A NEW QUOTE OPENS WITH, and nothing more: every quote carries its own
 * number from `0040` onwards, editable on its own screen.
 *
 * THIRTY.
 *
 * It is also the fallback when a row has no number of its own. `0040`
 * backfilled every quote that existed, so in practice that only covers a row
 * built in memory.
 */
export const DEFAULT_QUOTE_VALID_DAYS = 30;
/**
 * The range that number may take: the column's own (`app.sales_order.valid_days`
 * checks `between 1 and 365`, 0049). What a quote may PROMISE forward is
 * narrower, and is `MAX_QUOTE_HORIZON_DAYS`.
 *
 * Shared because both screens draw the limits beside the field and the server
 * refuses the same two ends -- the orders schemas and
 * `shared/document/update.ts` are where a number outside them becomes a
 * sentence rather than a Postgres check violation.
 */
export const MIN_QUOTE_VALID_DAYS = 1;
/**
 * WHAT THE COLUMN HOLDS, a number of days and nothing more (0049). Not 90:
 * with the validity set as a DATE, extending a quote issued three months ago
 * to a month from now is ~120 days counted from when it went out, though what
 * it promises forward is one month. The product rule is
 * `MAX_QUOTE_HORIZON_DAYS`.
 */
export const MAX_QUOTE_VALID_DAYS = 365;
/**
 * THE FURTHEST A QUOTE CAN PROMISE, COUNTED FROM TODAY: a price a workshop
 * writes today does not still stand a year from now -- a cotización good for
 * a year is a price list nobody can withdraw -- and three months is already
 * generous for a trade that buys its parts from somebody else's price list.
 *
 * FROM TODAY, not from sending: counted from sending, an old quote could not
 * be extended by a single day -- exactly when it is needed. Both clients
 * apply it drawing the calendar (`quoteValidityRange`) and the server on
 * save.
 */
export const MAX_QUOTE_HORIZON_DAYS = 90;
/**
 * Whether this business hands out cotizaciones at all -- the question both
 * «Mi negocio» screens ask before showing the validity field.
 *
 * A MODULE AND A FACE, never the name of a type: `pedidos` switched on, and
 * documents that wear the `SERVICIO` face -- a business that bills by
 * description (`ENCARGO`) writes a pedido and never a quote. `offersQuotes`
 * (faces.ts) is the one question allowed to know it, and this is the only
 * place that asks it this particular question, so the web and the phone
 * cannot end up asking different businesses.
 */
/** Whether this document has a day it is handed over on. A quote never does, at any status (0036). */
export function takesDueDay(doc) {
    return doc.kind !== "QUOTE";
}
export function issuesQuotes(modules, businessType) {
    return hasModule(modules, "pedidos") && offersQuotes(businessType);
}
/**
 * Which day «Agendar mantenimiento» opens on (spec §5): six months out.
 *
 * SIX MONTHS IS THE CANONICAL CASE, the one the plan writes both times it
 * names it -- the taller example and the end-to-end test. It is a PROPOSAL
 * and not a rule: whoever schedules the visit in that same meeting moves the
 * date right there, which is exactly what the date control is for.
 *
 * HERE AND NOT IN EACH CLIENT: the web and the phone have to open on the
 * same day, and two copies of this sum are two chances for them to drift
 * apart without anybody noticing.
 */
export function maintenanceDayFrom(today) {
    return addMonths(today, 6);
}
/**
 * «Agendar otra visita»: a ticket for the same customer about the same
 * objects, on the next open maintenance of those objects when there is one
 * (`nextDue`, or today when that one is already past), six months on
 * otherwise. The address both clients open
 * (`?customer=`, `?due=`, `?assets=`).
 */
export function anotherVisitParams(order, today, assetIds, nextDue = null, maintenanceIds = []) {
    return {
        kind: "ORDER",
        // The later of that maintenance and today, as `maintenanceVisitParams`:
        // an overdue one would open a ticket born late.
        due: nextDue === null ? maintenanceDayFrom(today) : nextDue > today ? nextDue : today,
        ...(order.partyId === null ? {} : { customer: order.partyId }),
        ...(assetIds.length === 0 ? {} : { assets: assetIds.join(",") }),
        // The open maintenances the new visit is for: it is linked to them (spec visita enmienda 10).
        ...(maintenanceIds.length === 0 ? {} : { maintenances: maintenanceIds.join(",") }),
    };
}
/**
 * Whether a quote has run out of the days it promised.
 *
 * READ, NEVER STORED (spec §3) -- the rule `app.obligation` already follows,
 * that a saved status is a second copy of a fact. Nobody writes `EXPIRED`,
 * and AN EXPIRED QUOTE CAN STILL BE ACCEPTED: what expiry does is take it out
 * of the list of what is waiting for an answer.
 *
 * Only a `SENT` quote can expire. A draft was never sent, and one already
 * answered -- accepted or rejected -- is not waiting for anything. The last
 * day it is good for is `issuedOn + validDays`, inclusive: the PDF promises
 * «vence en 15 días», and a paper that died on its fifteenth morning would
 * not be keeping that promise.
 */
export function isExpired(quote, today) {
    if (quote.status !== "SENT" || quote.issuedOn === null)
        return false;
    // THE NUMBER COMES OFF THE PAPER, and this is the only place that falls back
    // to the shared default. As an argument, every caller could pass a
    // different one, and seven screens could keep counting a number the paper
    // no longer says. A value nobody can pass wrong is worth more than a
    // parameter.
    return addDays(quote.issuedOn, quote.validDays ?? DEFAULT_QUOTE_VALID_DAYS) < today;
}
/**
 * HASTA QUÉ DÍA VALE ESTE PAPEL. La columna guarda un número de días y la
 * gente promete una fecha —— nadie dice «vale treinta días», dice «vale hasta
 * el 19 de octubre» ——, así que estas tres funciones son el puente entre las
 * dos cosas y viven aquí para que los dos clientes y el servidor cuenten igual.
 *
 * LA CUENTA ARRANCA EL DÍA QUE EL PAPEL SALIÓ —— `issued_on`, que
 * `setDocumentStatus` estampa una sola vez ——, porque es de ahí de donde cuenta
 * `isExpired`. Un borrador no ha salido y no tiene esa fecha: ahí se cuenta
 * desde hoy, que es lo único que se puede enseñar antes de mandarlo, y por eso
 * la pantalla avisa de que el plazo corre desde que se manda.
 */
export function quoteValidUntil(quote, today) {
    return addDays(quote.issuedOn ?? today, quote.validDays ?? DEFAULT_QUOTE_VALID_DAYS);
}
/** Los días que hay que guardar para prometer hasta ese día. La vuelta de `quoteValidUntil`. */
export function quoteValidDaysUntil(quote, until, today) {
    return daysBetween(quote.issuedOn ?? today, until);
}
/**
 * THE DUE DATE A NEW TICKET IS BORN WITH: its delivery day plus the
 * business's standing term, in days. Null when either is missing -- no day
 * to count from, or a business that gives no term.
 *
 * ONE SUM FOR EVERY DOOR THAT WRITES A TICKET (the lines door,
 * `ticketFromQuote`), so the two cannot count a day apart.
 */
export function defaultPaymentDueOn(deliveryDay, termDays) {
    if (deliveryDay === null || termDays === null)
        return null;
    return addDays(deliveryDay, termDays);
}
/**
 * THE DAY THE ACCOUNT BEHIND A PEDIDO FALLS DUE -- what `app.receivable.due_on`
 * must hold. A CLOSED ticket's own due date when it has one; otherwise the day
 * of `dueAt`, which is what every pedido used before 0065.
 *
 * ONLY ONCE CLOSED: while the work is pending, the account is late by its
 * delivery day, exactly as before -- the due date is a promise about paying
 * for work handed over, and nothing has been handed over yet.
 *
 * Every write of `due_on` for a pedido goes through this (delivering,
 * undelivering, editing the day or the due date), and so does every
 * «atrasado» test on a delivered ticket: an account that the paper says is
 * due on the 21st of next month is not late today.
 */
export function receivableDueOn(order, 
// The business's zone: which day `dueAt` falls on. REQUIRED: the server
// calls this, and a default would silently read nobody's active zone.
tz) {
    if (order.kind === "ORDER" && order.deliveredOn !== null && order.paymentDueOn !== null)
        return order.paymentDueOn;
    return order.dueAt === null ? null : dayOf(order.dueAt, tz);
}
/**
 * The term as typed in a box, turned into what the API takes. Empty is «no
 * term» (null); anything else must be a whole number of days from 0 to
 * `MAX_PAYMENT_TERM_DAYS`. Shared by the two boxes that ask it -- the
 * business default, on web and on the phone.
 */
export function parsePaymentTerm(text) {
    const trimmed = text.trim();
    if (trimmed === "")
        return { ok: true, days: null };
    if (!/^\d+$/.test(trimmed) || Number(trimmed) > MAX_PAYMENT_TERM_DAYS) {
        return { ok: false, message: `El plazo son días enteros, de 0 a ${String(MAX_PAYMENT_TERM_DAYS)}.` };
    }
    return { ok: true, days: Number(trimmed) };
}
/**
 * ENTRE QUÉ DOS DÍAS PUEDE CAER ESA FECHA.
 *
 * EL TOPE CUENTA DESDE HOY, NO DESDE EL ENVÍO. Si contara
 * desde el envío, una cotización emitida hace tres meses no se podría extender
 * ni un día —— que es justo cuando hace falta ——, porque ya habría gastado los
 * noventa. Lo que la regla protege es lo que se PROMETE hacia adelante: un
 * precio que un taller escribe hoy no sigue en pie dentro de un año.
 *
 * Y EL SUELO ES HOY, salvo en un borrador, donde lo más corto es mañana: un
 * papel que nace vencido no es un papel.
 */
export function quoteValidityRange(quote, today) {
    const floor = addDays(quote.issuedOn ?? today, MIN_QUOTE_VALID_DAYS);
    return {
        min: floor > today ? floor : today,
        max: addDays(today, MAX_QUOTE_HORIZON_DAYS),
    };
}
/**
 * Whether this collection may be recorded.
 *
 * A DELIVERED order still collects -- handing something over before being
 * paid is ordinary. A QUOTE never does, and a CANCELLED one does not: it keeps the payments it
 * already took, and a refund is an expense the owner notes by hand.
 */
export function canRecordPayment(order, amountCents) {
    // A QUOTE TAKES NO MONEY. Nobody has agreed to it, its price is an offer,
    // and the `cash_entry` would sit in the Libro allocated to a document no
    // list draws. The gate is here rather than in the server alone for this
    // file's usual reason: the phone, the browser and the server must answer
    // the same question identically, or a screen enables a button that 409s.
    if (order.kind === "QUOTE") {
        // «venta» written here, not read off the dictionary: these gates take no business type, and only
        // a quoting trade (the workshop, the print shop, the distributor) ever reaches one of them.
        return { ok: false, message: "Una cotización no se cobra: primero conviértela en venta." };
    }
    if (order.status === "CANCELLED") {
        return { ok: false, message: "Este pedido está cancelado." };
    }
    // AND THE MONEY IS THE KERNEL'S, not this file's: what is left of a ticket
    // is subtracted once, by `canSettle`, for every vertical (spec §2).
    return canSettle({ amountCents: order.priceCents, paidCents: order.paidCents }, amountCents);
}
/**
 * A QUOTE DOES NOT FREEZE when it leaves `DRAFT`: **what is charged is the
 * ticket**. The quote is not the money's document -- it takes no payments
 * (`canRecordPayment` refuses it) and does not appear in the Libro -- so
 * correcting a price on it rewrites no account.
 */
/**
 * QUÉ SE PUEDE TOCAR DE ESTE DOCUMENTO, en tres preguntas que no son la misma.
 *
 * `canEditDocument` ES LO QUE SE COBRA —— líneas, descripción, precio, fecha
 * de entrega, validez. `canAnnotateDocument` es lo que se CUENTA: la nota
 * interna, «lo que se hizo», la garantía. Se separan porque un ticket cerrado
 * congela lo primero y sigue admitiendo lo segundo: el técnico escribe lo que
 * hizo AL cerrar y lo corrige después, mientras que corregirle el precio a un
 * trabajo ya entregado reescribe una cuenta que el cliente ya tiene delante.
 *
 * `canDeleteDocument` es la tercera, y no se deduce de las otras dos: un
 * ticket cancelado no se edita y sin embargo se borra —— es justamente la
 * salida de un cancelado por error.
 *
 * AQUÍ Y NO EN CADA PANTALLA, por lo mismo que `canRecordPayment`: el
 * teléfono, el navegador y el servidor tienen que contestar idéntico, o una
 * pantalla dibuja un botón que contesta 409.
 */
/**
 * SI YA PARIÓ SU TICKET —— Y AUSENTE CUENTA COMO QUE NO.
 *
 * NO ES PEDANTERÍA SOBRE `undefined`: el campo es nuevo, y una fila leída por
 * un bundle viejo, o sacada de una caché escrita antes del despliegue, llega
 * sin él. Tratar esa ausencia como «sí tiene ticket» dejaría la cotización
 * bloqueada, en sólo lectura, sin nada en pantalla que explique por qué ——
 * hasta que alguien recargue. Al revés el peor caso es un botón que el
 * servidor refusa con su frase, que es exactamente lo que el servidor está
 * ahí para hacer.
 *
 * ESTAS VERJAS SON LA CARA AMABLE DE LA REGLA, nunca la que la sostiene: la
 * que la sostiene es `ticketFromQuote` con la fila bloqueada.
 */
function hasTicket(order) {
    return (order.ticketId ?? null) !== null;
}
/**
 * Whether this document may be deleted outright.
 *
 * BORRAR NO ES CANCELAR: borrar es «esto no debió escribirse nunca», cancelar es
 * «esto pasó y se cayó». Por eso lo único que cancela es un ticket con dinero
 * encima —— el anticipo cobrado de un trabajo que se anuló es un hecho, y
 * `cancelDocument` existe para conservarlo.
 *
 * LOS DOS FRENOS DEL TICKET SON DE INTEGRIDAD, no sólo de producto.
 * `allocation.receivable_id` cascadea (0050): borrar una cuenta con cobros se
 * llevaría las asignaciones y dejaría los ingresos sueltos en el Libro,
 * cuadrando un total contra nada. Y un ticket cerrado ya está en manos del
 * cliente.
 *
 * Y EL DE LA COTIZACIÓN TAMBIÉN: `order_service.quoted_from_id` es
 * `on delete set null` (0036), así que borrar la cotización de la que salió un
 * ticket le arrancaría el origen en silencio.
 */
export function canDeleteDocument(order) {
    if (order.kind === "QUOTE") {
        if (hasTicket(order)) {
            return { ok: false, message: "Esta cotización ya tiene una venta. Borra la venta primero." };
        }
        return { ok: true };
    }
    // EL DINERO SE NOMBRA ANTES QUE EL CIERRE aunque estorben los dos: deshacer
    // el cierre no desbloquea nada mientras los cobros sigan anotados, así que
    // la primera frase que alguien lee tiene que ser la del obstáculo que
    // sobrevive al otro.
    if (order.paidCents > 0) {
        return { ok: false, message: "Este pedido tiene cobros anotados. Bórralos primero en el Libro." };
    }
    if (order.status === "DELIVERED") {
        return { ok: false, message: "Este pedido está cerrado. Deshaz el cierre para borrarlo." };
    }
    return { ok: true };
}
/**
 * Whether what this document CHARGES may still be corrected.
 *
 * UNA COTIZACIÓN SE EDITA SIEMPRE, hasta que pare un ticket: una cotización
 * no toma dinero y no aparece en el Libro, así que corregirla no reescribe
 * ninguna cuenta.
 *
 * EL TICKET ES LA ÚNICA VERJA, y lo es porque el ticket COPIÓ las líneas
 * (`ticketFromQuote`). Corregir el papel después deja al cliente con un PDF
 * que dice una cosa y un trabajo que cobra otra, sin que nadie se entere.
 *
 * UN TICKET CERRADO CONGELA ESTO Y NADA MÁS: la puerta de vuelta es deshacer el
 * cierre, que es un toque. Un ticket cancelado no admite ninguna de las dos:
 * es un documento muerto.
 */
export function canEditDocument(order) {
    if (order.kind === "QUOTE") {
        if (hasTicket(order)) {
            return {
                ok: false,
                message: "Esta cotización ya tiene una venta: lo que se cobra es la venta. Corrígela ahí.",
            };
        }
        return { ok: true };
    }
    if (order.status === "DELIVERED") {
        return { ok: false, message: "Este pedido está cerrado. Deshaz el cierre para corregirlo." };
    }
    if (order.status === "CANCELLED") {
        return { ok: false, message: "Este pedido está cancelado." };
    }
    return { ok: true };
}
/**
 * Whether what this document SAYS may still be written: the internal note,
 * «lo que se hizo», the warranty.
 *
 * ABIERTA SOBRE UN TICKET CERRADO, que es el punto entero de separarla de
 * `canEditDocument`: la frase del técnico se escribe AL cerrar y se corrige
 * después («me faltó decir que también cambié la correa»), y mandarle a
 * deshacer el cierre para añadir una línea de texto sería cobrarle el precio
 * de una corrección de precio por escribir una nota.
 */
export function canAnnotateDocument(order) {
    if (order.kind === "QUOTE") {
        return hasTicket(order)
            ? { ok: false, message: "Esta cotización ya tiene una venta. Escribe en la venta." }
            : { ok: true };
    }
    if (order.status === "CANCELLED") {
        return { ok: false, message: "Este pedido está cancelado." };
    }
    return { ok: true };
}
/**
 * EL ALFABETO DEL NÚMERO DE UN DOCUMENTO, y es el de los localizadores de
 * avión: el estándar, sin complicarse.
 *
 * SEIS CARACTERES, que es lo que una persona dicta por teléfono de un tirón y
 * teclea sin equivocarse —— la razón de que un PNR mida seis y no doce.
 *
 * SIN LAS QUE SE CONFUNDEN AL DICTAR: fuera la I y el 1, fuera la O y el 0, y
 * fuera la Q, que a mano se lee como O. Es exactamente lo que las aerolíneas
 * quitaron de los suyos, y lo que evita que alguien teclee «CERO» donde había
 * una «O» y no encuentre su cotización.
 *
 * COLISIONAR ES IMPOSIBLE Y NO POR EL TAMAÑO: lo impide el índice único por
 * negocio (`0046`), y quien genera vuelve a sortear si el sorteado ya estaba.
 * El largo sólo decide cada cuánto hay que sortear dos veces —— con 31^6 ≈ 887
 * millones por negocio, nunca en la práctica —— y cuántos caracteres se leen en
 * voz alta.
 */
export const ORDER_CODE_ALPHABET = "23456789ABCDEFGHJKLMNPRSTUVWXYZ";
export const ORDER_CODE_LENGTH = 6;
